import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import FolderTree from '@/components/doc-control/FolderTree'
import { buildFolderTree, folderBreadcrumb, folderLabels } from '../lib/folders'
import type { PhotoFolder } from '../types'

export const NOT_EMPTY = 'Photos_error_folder_not_empty'

interface Props {
  folder: PhotoFolder | null
  folders: PhotoFolder[]
  /** Resolves to the server's error key/message, or null when the delete worked. */
  onSubmit: (targetFolderId: string | null) => Promise<{ key: string; text: string } | null>
  onClose: () => void
}

const DeleteFolderDialog: React.FC<Props> = ({ folder, folders, onSubmit, onClose }) => {
  const { t } = useTranslation()
  const [target, setTarget] = useState<string | null>(null)
  const [needsTarget, setNeedsTarget] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const hasChildren = !!folder && folders.some((x) => x.parentId === folder.id)
  const pickTarget = !!folder && (folder.photoCount > 0 || hasChildren || needsTarget)

  useEffect(() => {
    setTarget(null)
    setNeedsTarget(false)
    setError(null)
  }, [folder])

  // Everything except the folder being deleted and its subtree.
  const tree = useMemo(
    () => (folder ? buildFolderTree(folders.filter((x) => folderBreadcrumb(folders, x.id).every((a) => a.id !== folder.id)), folderLabels(t)) : []),
    [folder, folders, t],
  )

  const submit = async () => {
    setSaving(true)
    const failure = await onSubmit(pickTarget ? target : null)
    setSaving(false)
    if (!failure) return
    // A folder holding only soft-deleted photos looks empty here but the server still wants a target.
    if (failure.key === NOT_EMPTY) setNeedsTarget(true)
    setError(failure.text)
  }

  return (
    <Dialog open={!!folder} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('Photos_delete_folder')}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {pickTarget ? t('Photos_delete_folder_target', { name: folder?.name }) : t('Photos_delete_folder_confirm', { name: folder?.name })}
        </p>
        {pickTarget && (
          <div className="max-h-64 overflow-y-auto">
            <FolderTree folders={tree} selectedId={target} onSelect={setTarget} showCounts={false} aria-label={t('Photos_folders')} />
          </div>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('Common_cancel')}</Button>
          <Button variant="destructive" onClick={submit} disabled={saving || (pickTarget && !target)}>
            {saving && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Common_delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default DeleteFolderDialog
